import React, { FormEvent, useEffect, useMemo, useState } from "react";
import {
  ClinicianPortalApiError,
  clinicianPortalApi,
} from "../api/clinicianPortalApi";
import { ErrorState, LoadingState } from "../components/FeedbackStates";
import {
  Breadcrumbs,
  ClinicianPortalLayout,
  PortalHeader,
} from "../components/PortalLayout";
import { PortalLink } from "../components/PortalLink";
import { useAdminDirectory } from "../hooks/useAdminDirectory";
import { usePortalMutation } from "../hooks/usePortalMutation";
import {
  navigateWithinPortal,
  withClinicianCode,
} from "../routing/portalRoutes";

interface FormErrors {
  clinicName?: string;
  status?: string;
  therapists?: string;
}

function safeApiMessage(error: ClinicianPortalApiError, fallback: string) {
  return error.message.trim() || fallback;
}

function updateErrorMessage(error: unknown): FormErrors & { form?: string } {
  if (!(error instanceof ClinicianPortalApiError)) {
    return { form: "Clinic could not be saved. Try again." };
  }

  switch (error.code) {
    case "DUPLICATE_CLINIC":
      return { clinicName: "A clinic with this name already exists." };
    case "INVALID_CLINIC_NAME":
      return {
        clinicName: safeApiMessage(error, "Enter a valid clinic name."),
      };
    case "INVALID_STATUS":
      return { status: safeApiMessage(error, "Select a valid status.") };
    case "THERAPIST_NOT_FOUND":
      return {
        therapists: safeApiMessage(
          error,
          "One or more selected therapists are no longer available. Refresh and try again.",
        ),
      };
    case "NOT_AUTHORIZED":
    case "NOT_FOUND":
      return { form: "This clinic is unavailable." };
    default:
      return {
        form: safeApiMessage(error, "Clinic could not be saved. Try again."),
      };
  }
}

export function EditClinicPage({
  clinicianCode,
  clinicId,
}: {
  clinicianCode: string;
  clinicId: string;
}) {
  const directoryState = useAdminDirectory(clinicianCode);
  const mutation = usePortalMutation();
  const [clinicName, setClinicName] = useState("");
  const [status, setStatus] = useState("Active");
  const [selectedTherapistIds, setSelectedTherapistIds] = useState<string[]>(
    [],
  );
  const [initialized, setInitialized] = useState(false);
  const [validationErrors, setValidationErrors] = useState<FormErrors>({});
  const directory =
    directoryState.status === "success" ||
    directoryState.status === "refreshing"
      ? directoryState.data
      : undefined;
  const clinic = useMemo(
    () => directory?.clinics.clinics.find((item) => item.id === clinicId),
    [clinicId, directory],
  );
  const available = Boolean(
    directory?.clinics.found &&
      directory.clinics.authorized &&
      directory.clinics.therapist?.role === "admin" &&
      directory.therapists.found &&
      directory.therapists.authorized &&
      directory.therapists.therapist?.role === "admin",
  );

  useEffect(() => {
    if (!clinic || initialized) return;
    setClinicName(clinic.clinicName);
    setStatus(clinic.status || "Active");
    setSelectedTherapistIds(clinic.therapistIds);
    setInitialized(true);
  }, [clinic, initialized]);

  const isSubmitting = mutation.state.status === "pending";
  const apiErrors =
    mutation.state.status === "error"
      ? updateErrorMessage(mutation.state.error)
      : {};
  const errors = { ...validationErrors, ...apiErrors };

  const clearErrors = () => {
    setValidationErrors({});
    mutation.reset();
  };

  const toggleTherapist = (therapistId: string) => {
    clearErrors();
    setSelectedTherapistIds((current) =>
      current.includes(therapistId)
        ? current.filter((id) => id !== therapistId)
        : [...current, therapistId],
    );
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitting) return;

    const normalizedName = clinicName.trim();
    const nextErrors: FormErrors = {};
    if (!normalizedName) nextErrors.clinicName = "Clinic name is required.";
    if (status !== "Active" && status !== "Inactive") {
      nextErrors.status = "Select a valid status.";
    }
    setValidationErrors(nextErrors);
    mutation.reset();
    if (Object.keys(nextErrors).length) return;

    const result = await mutation.run("update-clinic", (signal) =>
      clinicianPortalApi.updateClinic(
        clinicianCode,
        clinicId,
        {
          clinicName: normalizedName,
          status,
          therapistIds: selectedTherapistIds,
        },
        signal,
      ),
    );
    if (result?.updated) {
      navigateWithinPortal(
        withClinicianCode("/clinician-portal/admin/clinics", clinicianCode),
      );
    }
  };

  return (
    <ClinicianPortalLayout
      activeGlobalKey="clinics"
      clinicianCode={clinicianCode}
      therapistRole={directory?.clinics.therapist?.role}
    >
      <PortalHeader
        title={clinic?.clinicName || "Edit Clinic"}
        clinicianName={
          available ? directory?.clinics.therapist?.therapistName : undefined
        }
        breadcrumbs={
          <Breadcrumbs
            clinicianCode={clinicianCode}
            items={[
              { label: "Administration" },
              { label: "Clinics", path: "/clinician-portal/admin/clinics" },
              { label: "Edit Clinic" },
            ]}
          />
        }
      />
      <div className="cp-page-content">
        {directoryState.status === "loading" ? (
          <LoadingState label="Loading clinic" />
        ) : null}
        {directoryState.status === "error" ? (
          <ErrorState
            error={directoryState.error}
            onRetry={directoryState.refresh}
          />
        ) : null}
        {directory && !available ? (
          <ErrorState error={new Error("Clinic editing is unavailable")} />
        ) : null}
        {directory && available && !clinic ? (
          <ErrorState error={new Error("Clinic not found")} />
        ) : null}
        {directory && available && clinic && initialized ? (
          <section
            className="cp-record-form"
            aria-labelledby="edit-clinic-heading"
          >
            <div className="cp-record-form__header">
              <h2 id="edit-clinic-heading">Edit Clinic</h2>
              <p>Update the clinic and its clinician assignments.</p>
            </div>
            <form onSubmit={handleSubmit} noValidate>
              <div className="cp-form-field">
                <label htmlFor="clinic-name">
                  Clinic Name <span aria-hidden="true">*</span>
                </label>
                <input
                  id="clinic-name"
                  className="cp-form-input"
                  type="text"
                  value={clinicName}
                  required
                  aria-invalid={Boolean(errors.clinicName)}
                  aria-describedby={
                    errors.clinicName ? "clinic-name-error" : undefined
                  }
                  onChange={(event) => {
                    clearErrors();
                    setClinicName(event.target.value);
                  }}
                />
                {errors.clinicName ? (
                  <p className="cp-form-error" id="clinic-name-error">
                    {errors.clinicName}
                  </p>
                ) : null}
              </div>

              <div className="cp-form-field">
                <label htmlFor="clinic-status">
                  Status <span aria-hidden="true">*</span>
                </label>
                <select
                  id="clinic-status"
                  className="cp-form-input"
                  value={status}
                  required
                  aria-invalid={Boolean(errors.status)}
                  aria-describedby={
                    errors.status ? "clinic-status-error" : undefined
                  }
                  onChange={(event) => {
                    clearErrors();
                    setStatus(event.target.value);
                  }}
                >
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
                {errors.status ? (
                  <p className="cp-form-error" id="clinic-status-error">
                    {errors.status}
                  </p>
                ) : null}
              </div>

              <fieldset
                className="cp-form-field cp-form-fieldset"
                aria-describedby={
                  errors.therapists ? "therapists-error" : undefined
                }
              >
                <legend>Therapists</legend>
                {directory.therapists.therapists.length ? (
                  <div className="cp-checkbox-list">
                    {directory.therapists.therapists.map((therapist) => (
                      <label className="cp-checkbox-option" key={therapist.id}>
                        <input
                          type="checkbox"
                          checked={selectedTherapistIds.includes(therapist.id)}
                          onChange={() => toggleTherapist(therapist.id)}
                        />
                        <span>
                          <strong>{therapist.therapistName || "—"}</strong>
                          <small>{therapist.email || "—"}</small>
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="cp-form-empty">
                    No therapist accounts are currently available. This clinic
                    can remain unassigned.
                  </p>
                )}
                {errors.therapists ? (
                  <p className="cp-form-error" id="therapists-error">
                    {errors.therapists}
                  </p>
                ) : null}
              </fieldset>

              {apiErrors.form ? (
                <p className="cp-form-error cp-form-error--panel" role="alert">
                  {apiErrors.form}
                </p>
              ) : null}

              <div className="cp-form-actions cp-form-actions--sticky">
                <PortalLink
                  className="cp-button cp-button--secondary"
                  path="/clinician-portal/admin/clinics"
                  clinicianCode={clinicianCode}
                >
                  Cancel
                </PortalLink>
                <button
                  type="submit"
                  className="cp-button cp-button--primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? "Saving…" : "Save Changes"}
                </button>
              </div>
            </form>
          </section>
        ) : null}
      </div>
    </ClinicianPortalLayout>
  );
}
